import { AppDisplay } from '@ui/applications/AppDisplay';
import { ColumnType } from 'antd/es/table';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { NotificationFrequencyTag } from './NotificationFrequencyTag';
import React from 'react';
import { SonarStakeholder } from '@ui/SonarStakeholder';

import {
	DataSecurityMonitor,
	DataSecurityMonitorType,
	MonitoringWindowType
} from '@sonar-software/sonar-core';
import { monitorParentType, MonitorTypeTag } from './MonitorTypeTag';
import { Space, Switch } from 'antd';

export type ToggleMonitorMute = (mute: boolean, id: number) => void;
export type ToggleMonitorSubscription = (subscribe: boolean, id: number) => void;

type ColumnDefinition = ColumnType<DataSecurityMonitor> & { dataIndex: keyof DataSecurityMonitor | Array<string> };

export const nameColumn: ColumnDefinition = {
	className: 'monitor-name',
	dataIndex: 'name',
	fixed: true,
	key: 'name',
	title: COPY_SPECIFIC.NAME,
	width: 160
};

export const typeColumn: ColumnDefinition = {
	dataIndex: [ 'monitorConfig', 'monitorTypeId' ],
	key: 'monitorTypeId',
	render: (monitorTypeId: number) => <MonitorTypeTag monitorTypeId={ monitorTypeId } />,
	title: COPY_SPECIFIC.TYPE,
	width: 150
};

export const appColumn: ColumnDefinition = {
	dataIndex: 'applicationId',
	key: 'applicationId',
	render: (applicationId) => (applicationId ? <AppDisplay appId={ applicationId } /> : null),
	title: COPY_SPECIFIC.APP,
	width: 120
};

export const emailColumn: ColumnDefinition = {
	dataIndex: 'stakeholderIds',
	key: 'stakeholderIds',
	render: (stakeholderIds: number[]) =>
		<Space
			direction='horizontal'
			size={ [ 32, 8 ] }
			wrap>
			{ stakeholderIds.map((id) =>
				<SonarStakeholder
					key={ id }
					stakeholderId={ id } />) }
		</Space>,
	title: COPY_SPECIFIC.EMAIL_NOTIFICATIONS,
	width: 170
};

export const frequencyColumn: ColumnDefinition = {
	dataIndex: [ 'monitorConfig', 'monitoringWindow' ],
	key: 'monitoringWindow',
	render: (monitoringWindow: MonitoringWindowType, record: DataSecurityMonitor) =>
		<NotificationFrequencyTag frequency={ monitoringWindow ?? record.notificationSchedule } />,
	title: COPY_SPECIFIC.NOTIFICATION_FREQUENCY,
	width: 120
};

export const muteColumn = (toggle: ToggleMonitorMute): ColumnDefinition => ({
	align: 'center',
	dataIndex: 'isMuted',
	key: 'mute',
	render: (_, record: DataSecurityMonitor) => {
		const { isMuted, ...monitor } = record;

		const id = monitorParentType[ monitor.monitorConfig.monitorTypeId as DataSecurityMonitorType ] ??
			monitor.monitorConfig.monitorTypeId;

		if (id === DataSecurityMonitorType.Personal) {
			return null;
		}

		return (
			<Switch
				checked={ record.isMuted }
				onChange={ (mute) => toggle(mute, monitor.id) } />
		);
	},
	title: COPY_SPECIFIC.GLOBAL_MUTE,
	width: 120
});

export const subscribeColumn = (userId: number, toggle: ToggleMonitorSubscription): ColumnDefinition => ({
	align: 'center',
	dataIndex: 'stakeholderIds',
	key: 'subscribe',
	render: (stakeholderIds: Array<number>, record: DataSecurityMonitor) =>
		<Switch
			checked={ stakeholderIds.includes(userId) }
			onChange={ (subscribe) => toggle(subscribe, record.id) } />,
	title: COPY_SPECIFIC.SUBSCRIBE,
	width: 120
});

export function getColumns(
	readOnly: boolean,
	userId: number,
	toggleMute: ToggleMonitorMute,
	toggleSubscribe: ToggleMonitorSubscription
):
Array<ColumnDefinition> {
	if (readOnly) {
		return [
			nameColumn,
			typeColumn,
			appColumn,
			emailColumn,
			frequencyColumn,
			subscribeColumn(userId, toggleSubscribe)
		];
	}

	return [
		nameColumn,
		typeColumn,
		appColumn,
		emailColumn,
		frequencyColumn,
		muteColumn(toggleMute),
		subscribeColumn(userId, toggleSubscribe)
	];
}
